import React from "react";
import { Modal } from "@hid-galaxy-ui/galaxy-react";
import ScreenshotMedia from "./ScreenshotMedia";
import { ScreenshotItem } from "./screenshotsConfig";
import "./ScreenshotModals.scss";

type ScreenshotPreviewModalProps = {
  screenshots: ScreenshotItem[];
  initialIndex: number;
  onClose: () => void;
};

const ChevronIcon: React.FC<{ direction: "left" | "right" }> = ({ direction }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d={direction === "left" ? "m12.5 4.5-5.5 5.5 5.5 5.5" : "m7.5 4.5 5.5 5.5-5.5 5.5"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ScreenshotPreviewModal: React.FC<ScreenshotPreviewModalProps> = ({
  screenshots,
  initialIndex,
  onClose,
}) => {
  const [activeIndex, setActiveIndex] = React.useState(initialIndex);
  const total = screenshots.length;
  const hasMultiple = total > 1;
  const active = screenshots[activeIndex];

  const showPrevious = React.useCallback(() => {
    setActiveIndex((index) => (index - 1 + total) % total);
  }, [total]);

  const showNext = React.useCallback(() => {
    setActiveIndex((index) => (index + 1) % total);
  }, [total]);

  React.useEffect(() => {
    if (!hasMultiple) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") showPrevious();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasMultiple, showPrevious, showNext]);

  if (!active) return null;

  return (
    <Modal
      headerText={active.name}
      width={1100}
      onClose={onClose}
      className="screenshots-modal screenshot-preview-modal"
    >
      <div className="screenshot-preview-modal__stage">
        {hasMultiple ? (
          <button
            type="button"
            className="screenshot-preview-modal__nav screenshot-preview-modal__nav--prev"
            aria-label="Previous screenshot"
            onClick={showPrevious}
          >
            <ChevronIcon direction="left" />
          </button>
        ) : null}

        <ScreenshotMedia
          key={active.id}
          url={active.url}
          name={active.name}
          mimeType={active.mimeType}
          className="screenshot-preview-modal__media"
        />

        {hasMultiple ? (
          <button
            type="button"
            className="screenshot-preview-modal__nav screenshot-preview-modal__nav--next"
            aria-label="Next screenshot"
            onClick={showNext}
          >
            <ChevronIcon direction="right" />
          </button>
        ) : null}
      </div>

      {hasMultiple ? (
        <p className="screenshot-preview-modal__counter">
          {activeIndex + 1} / {total}
        </p>
      ) : null}
    </Modal>
  );
};

export default ScreenshotPreviewModal;
